<script setup lang="ts">
import type { PublicOrganization } from "~/composables/useRechercheData";
import {
  FRENCH_DEPARTMENTS,
  regionBySlug,
  regionInLabel,
} from "#shared/utils/frenchGeo";
import {
  GEO_HUB_MIN_REGION,
  isOrgIndexable,
} from "#shared/utils/geoHubThresholds";

const { t } = useI18n({ useScope: "local" });

/**
 * Étage RÉGION de l'annuaire — /associations/region/occitanie
 *
 * Seuil le plus haut de l'arbre (GEO_HUB_MIN_REGION) : plus le périmètre est
 * large, plus il faut d'associations pour que la page apporte quelque chose
 * que la page département n'apporte pas déjà. Une région qui liste 2
 * associations réparties sur 13 départements n'a aucune valeur propre.
 *
 * Même règle de non-redondance qu'à l'étage département : si tout tient dans
 * un seul département, on garde le niveau le plus précis.
 *
 * Mesuré sur la prod le 2026-08-06 : une seule région franchit le seuil
 * (Île-de-France, 16 associations indexables). Les autres restent en noindex
 * et s'allumeront d'elles-mêmes à mesure que l'inventaire grandit — aucune
 * intervention nécessaire, aucun code à changer.
 */
const route = useRoute();
const regionSlug = (route.params as Record<string, string>).region ?? "";

const region = regionBySlug(regionSlug);
if (!region) {
  throw createError({ statusCode: 404, statusMessage: t('erreur.regionIntrouvable') });
}

const { data, pending } = await useAsyncData(`orgs-region-${regionSlug}`, async () => {
  try {
    return await $fetch<{
      name: string;
      indexableCount: number;
      /** `departement` : code du département de l'asso (« 34 », « 977 »), cf. by-geo. */
      organizations: Array<PublicOrganization & { city: string | null; departement?: string | null }>;
      cities: Array<{ slug: string; name: string; count: number }>;
    }>(`/api/organizations/by-geo?level=region&slug=${regionSlug}`);
  } catch {
    return { name: region.name, indexableCount: 0, organizations: [], cities: [] };
  }
});

const organizations = computed(() => data.value?.organizations ?? []);
const cities = computed(() => data.value?.cities ?? []);
const indexableCount = computed(
  () => organizations.value.filter((o) => isOrgIndexable(o)).length,
);

// Départements peuplés de la région, pour le maillage descendant.
const departments = computed(() => {
  const counts = new Map<string, number>();
  for (const org of organizations.value) {
    if (!isOrgIndexable(org)) continue;
    // Département calculé par by-geo (morceaux 307, à défaut le texte) :
    // la page ne relit plus l'adresse elle-même.
    const code = org.departement;
    if (code) counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  return FRENCH_DEPARTMENTS.filter((d) => counts.has(d.code))
    .map((d) => ({ ...d, count: counts.get(d.code) ?? 0 }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "fr"));
});

const inLabel = regionInLabel(region);
const canonicalUrl = `https://donoor.org/associations/region/${region.slug}`;

const seoTitle = computed(
  () => t('seo.title', { inLabel }),
);
const seoDescription = computed(
  () =>
    t('seo.description', { count: indexableCount.value, s: indexableCount.value > 1 ? "s" : "", inLabel }),
);

const isRedundantWithSingleDepartment = computed(() => departments.value.length <= 1);
const shouldNoindex = computed(
  () =>
    !pending.value &&
    (indexableCount.value < GEO_HUB_MIN_REGION ||
      isRedundantWithSingleDepartment.value),
);
const robotsContent = useRobotsContent(shouldNoindex);

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: "https://donoor.org/og-image.png",
  ogUrl: canonicalUrl,
  ogLocale: "fr_FR",
  twitterCard: "summary",
  robots: () => robotsContent.value,
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoTitle.value,
        description: seoDescription.value,
        url: canonicalUrl,
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Donoor", item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: t('breadcrumb.associations'), item: "https://donoor.org/recherche/associations" },
            { "@type": "ListItem", position: 3, name: region.name, item: canonicalUrl },
          ],
        },
        // ItemList (SEO 2026-08-29) : sans lui, la page est « une page avec des
        // liens » pour les moteurs et les LLM — avec, c'est une LISTE de N
        // associations, ce que les hubs ville et catégorie déclarent déjà.
        ...(organizations.value.length > 0 && {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: organizations.value.length,
            itemListElement: organizations.value.slice(0, 50).map((o, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: { "@type": "NGO", name: o.name, url: `https://donoor.org/associations/${o.slug}` },
            })),
          },
        }),
      }),
    },
  ]),
});
</script>

<template>
  <div class="min-h-screen bg-white">
    <RechercheUnifiedHero
      active-type="associations"
      :h1="t('hero.titre', { inLabel })"
      :intro="t('hero.intro', { count: indexableCount, s: indexableCount > 1 ? 's' : '', inLabel })"
    />

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
        <nav v-if="departments.length" class="mb-8" :aria-label="t('departements.aria')">
          <h2 class="text-lg font-semibold text-gray-900 mb-3">{{ t('departements.titre') }}</h2>
          <ul class="flex flex-wrap gap-2">
            <li v-for="d in departments" :key="d.code">
              <NuxtLink
                :to="`/associations/departement/${d.slug}`"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                {{ d.name }} <span class="text-xs text-gray-400">{{ d.count }}</span>
              </NuxtLink>
            </li>
          </ul>
        </nav>

        <nav v-if="cities.length" class="mb-10" :aria-label="t('villes.aria')">
          <h2 class="text-lg font-semibold text-gray-900 mb-3">{{ t('villes.titre') }}</h2>
          <ul class="flex flex-wrap gap-2">
            <li v-for="c in cities" :key="c.slug">
              <NuxtLink
                :to="`/associations/ville/${c.slug}`"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                {{ c.name }} <span class="text-xs text-gray-400">{{ c.count }}</span>
              </NuxtLink>
            </li>
          </ul>
        </nav>

        <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div v-for="i in 6" :key="i" class="h-56 rounded-2xl bg-gray-100 animate-pulse" />
        </div>

        <div v-else-if="organizations.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <RechercheOrganizationCard v-for="(org, i) in organizations" :key="org.id" :org="org" :priority="i < 3" />
        </div>

        <div v-else class="text-center py-16">
          <UIcon name="i-lucide-building-2" class="size-12 text-gray-300 mx-auto mb-4" />
          <p class="text-gray-500 mb-4">{{ t('vide.message', { inLabel }) }}</p>
          <NuxtLink to="/recherche/associations" class="text-sm text-(--color-main) hover:underline">
            {{ t('vide.lien') }}
          </NuxtLink>
        </div>
      </UContainer>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../../i18n/pages/pages/associations/region/[region].json"></i18n>
